import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function ReactivationPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reactivating, setReactivating] = useState(false);
  const [reactivated, setReactivated] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/api/courses/public/reactivate/${token}`)
      .then(res => {
        setInfo(res.data);
        if (res.data.status !== "cancelled") setReactivated(true);
      })
      .catch(err => setError(err.response?.data?.detail || "Ungültiger Link"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const res = await axios.post(`${API_BASE}/api/courses/public/reactivate/${token}`);
      if (res.data.status === "already_active") {
        setReactivated(true);
      } else {
        setReactivated(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler bei der Reaktivierung");
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 flex items-center justify-center px-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <img src="/fitmacher_logo.png" alt="Fitmacher Schwimmausbildung" style={{ maxWidth: 200, margin: "0 auto" }} />
        </div>

        <div className="rounded-2xl bg-white shadow-lg p-6 sm:p-8">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-sky-600 border-r-transparent" />
              <p className="mt-4 text-slate-500">Lade Daten...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8" data-testid="reactivation-error">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-red-700">Nicht möglich</h3>
              <p className="mt-2 text-slate-600">{error}</p>
            </div>
          ) : reactivated ? (
            <div className="text-center py-4" data-testid="reactivation-success">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900">Stornierung zurückgenommen!</h3>
              <p className="mt-3 text-slate-600">
                Die Anmeldung für <strong>{info?.child_name}</strong> zum Kurs <strong>{info?.course_title}</strong> ist wieder aktiv.
              </p>
              <p className="mt-2 text-sm text-slate-400">Der Platz ist nun wieder für Ihr Kind reserviert.</p>
            </div>
          ) : (
            <div data-testid="reactivation-form">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Stornierung zurücknehmen</h3>
              <div className="rounded-xl bg-slate-50 p-4 space-y-2">
                <p className="text-sm text-slate-600"><strong>Kind:</strong> {info?.child_name}</p>
                <p className="text-sm text-slate-600"><strong>Kurs:</strong> {info?.course_title}</p>
              </div>

              {info?.is_full ? (
                <div className="mt-6 rounded-xl bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-800 font-medium">
                    Leider ist der Kurs zwischenzeitlich ausgebucht. Die Stornierung kann nicht mehr zurückgenommen werden.
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    Bitte kontaktieren Sie uns bei Fragen.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                    <p className="text-sm text-emerald-800">
                      <strong>Gute Nachricht:</strong> Es {info?.free_spots === 1 ? "ist noch 1 Platz" : `sind noch ${info?.free_spots} Plätze`} frei. Sie können die Stornierung zurücknehmen.
                    </p>
                  </div>
                  <button
                    data-testid="confirm-reactivate-btn"
                    onClick={handleReactivate}
                    disabled={reactivating}
                    className="mt-6 w-full rounded-xl bg-emerald-600 px-6 py-3 font-semibold text-white shadow hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  >
                    {reactivating ? "Wird verarbeitet..." : "Stornierung jetzt zurücknehmen"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="text-center mt-6 text-sm text-slate-400">
          Fragen? <a href="mailto:schwimmausbildung@sfw-berlin.org" className="text-slate-500 underline hover:text-slate-700">schwimmausbildung@sfw-berlin.org</a>
        </div>
      </div>
    </div>
  );
}
